import { Calendar, CheckCircle, CreditCard, MapPin, RefreshCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { serviceInstructions, serviceCategories } from "../config/services";
import { Button } from "../Components/General/Button";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useWizard } from "../Contexts/WizardContext";
import { formatDateToUSA } from "../config/utils";

function formatTimeWindow(val) {
    let res
    switch (val) {
        case 'PM':
            res = '12:00PM - 5:00PM'
            break
        case 'AM':
            res = '7:00AM - 12:00PM'
            break
        case 'full_day':
            res = '7:00AM - 5:00PM'
            break
        default:
            res = '7:00AM - 5:00PM'
    }
    return res
}

export default function ThankYou() {
    const navigate = useNavigate()
    const { formData, resetAll } = useWizard();
    
    const address = formData.address.formattedAddress
    const paymentOption = formData.paymentOption
    const formatted = formData.appointments.map(item => item.services.map(service => {
      const subServiceName = service.serviceName == 'Yard Cleanup' ? item.schedulingBucketId : service.serviceId
      return {
          ...service,
          service_type: subServiceName,
      }
  })).flat()
    const uniqueServiceIds = [...new Set(formatted.map(el => el.service_type))]
    
    const scheduledAppointments = formData.appointments.map(item => {
      const mappedCat = serviceCategories.find(el => el.id == item.service_type)
      const formattedCat = mappedCat ? mappedCat.name : item.service_type

      const subServicesIds = item.services.map(s => s.serviceId)
      const mappedSubServices = mappedCat ? 
          (formattedCat == 'Yard Cleanup' ? 
              mappedCat.services[0].tiers.filter(el => subServicesIds.includes(el.id)).map(el => el.label) 
              : mappedCat.services.filter(el => subServicesIds.includes(el.id)).map(el => el.name)
          ) 
      : subServicesIds
      return {
          service_type: `${formattedCat}: ${mappedSubServices.join(', ')}`,
          date: item.appointmentDate,
          time_window: formatTimeWindow(item.time_window)
      }
  })
    
    return (
        <div className="w-full max-w-4xl mx-auto p-4 text-left">
            {/* HEADER */}
            <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                    <CheckCircle className="w-16 h-16 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">
                    {scheduledAppointments.length > 1 ? 'Appointments Booked Successfully!' : 'Appointment Booked Successfully!'}
                </h2>
                <p className="text-lg text-gray-600"> Thank you for choosing ACA Landscape. We're excited to serve you!</p>
            </div>
            {/* HEADER */}

            {(scheduledAppointments.length) ? <>

                {/* DETAILS BLOCK */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
                    <h3 className="font-semibold text-green-800 mb-4 flex items-center gap-2 border-b border-teal-600 pb-3"> 
                        <Calendar className="w-5 h-5"/> Your Appointment Details
                    </h3>

                    <div className="space-y-4">
                        {scheduledAppointments.map((service, idx) => {
                            console.log('service.', service)
                            console.log('service.service_type', service.service_type)
                            return (
                                <div key={idx} className="border-b border-green-200 last:border-0 pb-3 last:pb-0">
                                    <p className="text-green-800 font-medium mb-1">
                                        {formatDateToUSA(service.date)} | {service.time_window} | {service.service_type}
                                    </p>
                                </div>
                            )
                        })}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mt-4 pt-4 border-t border-green-200 text-left">
                        <div>
                            <p className="text-green-700 font-medium">Location</p>
                            <p className="text-green-800 flex items-start gap-1">
                                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                {address}
                            </p>
                        </div>
                        <div>
                            <p className="text-green-700 font-medium">Payment</p>
                            <p className="text-green-800 flex items-center gap-1">
                                <CreditCard className="w-4 h-4" />
                                {paymentOption === 'now' ? 'Paid in advance' : 'Card saved for later payment'}
                            </p>
                        </div>
                    </div>
                </div>
                {/* DETAILS BLOCK */}
                        
                {/* INSTRUCTIONS */}
                {uniqueServiceIds.map(serviceId => {
                    const instructions = serviceInstructions[serviceId];
                    if (!instructions) {
                        console.log('instructions was not mapped for', serviceId)
                        console.log('all instructions', serviceInstructions)
                    }
                    return instructions && (
                    <div key={serviceId} className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
                        <h3 className="font-semibold text-gray-800 mb-4">{instructions.title}</h3>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div>
                                <h4 className="font-medium text-gray-700 mb-3">Preparation Checklist:</h4>
                                <ul className="space-y-2">
                                {instructions.items.map((item, index) => (
                                    <li key={index} className="flex items-start gap-2 text-gray-600">
                                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                        <span className="text-sm">{item}</span>
                                    </li>
                                ))}
                                </ul>
                            </div>
                            {instructions.videoId && (
                                <div>
                                    <h4 className="font-medium text-gray-700 mb-3">Service Overview Video:</h4>
                                    <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                                        <iframe
                                            className="w-full h-48"
                                            src={`https://www.youtube.com/embed/${instructions.videoId}`}
                                            title="Service Preparation Video"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">Watch this video to learn more about what to expect during your service.</p>
                                </div>
                            )}
                        </div>
                    </div>
                    );
                })}
                {/* INSTRUCTIONS */}

                {/* Next Steps */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 mt-4">
                    <h3 className="font-semibold text-blue-800 mb-3">What Happens Next?</h3>
                    <div className="space-y-3 text-sm text-blue-700">
                    <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                        <div>
                            <p className="font-medium">Confirmation Email</p>
                            <p>You'll receive a detailed confirmation email within the next few minutes.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                        <div>
                            <p className="font-medium">Schedule Refinement</p>
                            <p>You will receive an ETA the day prior to your service.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
                        <div>
                            <p className="font-medium">Service Day</p>
                            <p>Our professional team will arrive and complete service.</p>
                        </div>
                    </div>
                    {paymentOption === 'Pay Later' && (
                        <div className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</div>
                            <div>
                                <p className="font-medium">Payment Processing</p>
                                <p>Your saved payment method will be charged after service completion.</p>
                            </div>
                        </div>
                    )}
                    </div>
                </div>
                {/* Next Steps */}
            </> : ''}

             {/* Contact Information */}
             <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <h3 className="font-semibold text-gray-800 mb-3">Questions or Changes?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <p className="text-gray-600 mb-2">Call us:</p>
                        <p className="font-medium text-gray-800">(303) 853-8550</p>
                    </div>
                    <div>
                        <p className="text-gray-600 mb-2">Email us:</p>
                        <p className="font-medium text-gray-800">service@acalandscape.com</p>
                    </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">Please contact us at least 48 hours in advance if you need to reschedule or cancel your appointment.</p>
            </div>

            {/* Contact Information */}

            {/* Actions */}
            <div className="flex gap-4">
                <Button
                    label='Return to Website'
                    onClick={() => {
                        window.location.href = 'https://acalandscape.com/'
                    }}
                />
                <Button
                    label='Schedule Another Service'
                    onClick={() => {
                        resetAll()
                        navigate('/')
                    }}
                    primary
                />
            </div>
            
        </div>
    )    
}
